/**
 * Client-side catalog subscription coverage resolution.
 *
 * Subscriptions are fetched as raw rows and coverage is derived here, mirroring the
 * backend engine's rules: the subscribe engine keeps levels disjoint, a broad level
 * owns everything beneath it, and brand equality is exact — a null brand is the
 * brandless "No Brand" bucket, matching only items whose own brand is null.
 */

/** The query-param stand-in for a null scope: brand 0 is the brandless bucket, category 0 uncategorized. */
export const NO_REFERENCE_SENTINEL = 0

/** Find the whole-catalog subscription, or null when the catalog is not subscribed as a whole. */
export function findCatalogSubscription(subscriptions: CatalogSubscription[]): CatalogSubscription | null {
    return subscriptions.find((subscription) => subscription.level === "catalog") ?? null
}

/**
 * Find the brand-level subscription for a brand, or null when none exists.
 *
 * Pass null for the brandless "No Brand" bucket — brand equality is exact, so the
 * bucket's subscription never matches a real brand and vice versa.
 */
export function findBrandSubscription(
    subscriptions: CatalogSubscription[],
    brandId: number | null,
): CatalogSubscription | null {
    return (
        subscriptions.find((subscription) => subscription.level === "brand" && subscription.brand === brandId) ?? null
    )
}

/**
 * Find the category-level subscription for a category within a brand, or null when none exists.
 *
 * Category subscriptions are brand-scoped, so the same brand-equality rule applies:
 * pass null for the brandless "No Brand" bucket.
 */
export function findCategorySubscription(
    subscriptions: CatalogSubscription[],
    brandId: number | null,
    categoryId: number,
): CatalogSubscription | null {
    return (
        subscriptions.find(
            (subscription) =>
                subscription.level === "category" &&
                subscription.brand === brandId &&
                subscription.category === categoryId,
        ) ?? null
    )
}

/**
 * Find the subscription pointing directly at an item, or null when its coverage is inherited.
 *
 * Only an item-level row counts: this is what a per-row unsubscribe deletes, so an
 * item covered by a broader level deliberately reports null.
 */
export function findItemSubscription(
    subscriptions: CatalogSubscription[],
    itemId: number,
): CatalogSubscription | null {
    return (
        subscriptions.find((subscription) => subscription.level === "item" && subscription.catalog_item === itemId) ??
        null
    )
}

interface CoverageItemScope {
    id: number
    brand: number | null
    categories: { id: number }[]
}

/**
 * Report whether a subscription's scope covers an item.
 *
 * Mirrors the backend's effective-coverage rules: a whole-catalog subscription
 * covers anything, a brand subscription the items sharing its exact brand (null
 * matching only null), a category subscription the items filed under that category
 * that also share its brand, and an item subscription only itself.
 */
function subscriptionCoversItem(subscription: CatalogSubscription, item: CoverageItemScope): boolean {
    if (subscription.level === "catalog") {
        return true
    } else if (subscription.level === "brand") {
        return subscription.brand === item.brand
    } else if (subscription.level === "category") {
        return (
            subscription.brand === item.brand &&
            item.categories.some((category) => category.id === subscription.category)
        )
    } else {
        return subscription.catalog_item === item.id
    }
}

/** Find the subscription covering an item, or null when nothing covers it. */
export function findCoveringSubscription(
    subscriptions: CatalogSubscription[],
    item: CoverageItemScope,
): CatalogSubscription | null {
    return subscriptions.find((subscription) => subscriptionCoversItem(subscription, item)) ?? null
}
